import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLeads } from '../hooks/useLeads.js';
import { KanbanBoard } from '../components/crm/KanbanBoard.js';
import { LeadDetailDialog } from '../components/crm/LeadDetailDialog.js';
import { LeadForm } from '../components/crm/LeadForm.js';
import type { Lead, PipelineEtapa } from '@azoupvendas/shared';

export function LeadsList() {
  const { leads, byEtapa, isLoading, isError, createLead, updateEtapa, updateLead } = useLeads();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [editLead, setEditLead] = useState<Lead | null>(null);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [busca, setBusca] = useState('');

  const total = leads.length;
  const valorTotal = leads.reduce((acc, l) => acc + (l.valor_estimado ?? 0), 0);

  // AIDEV-NOTE: Filtro client-side sobre dados já em cache — evita re-fetch a cada keystroke
  const filteredByEtapa = busca
    ? (Object.fromEntries(
        (Object.entries(byEtapa) as [PipelineEtapa, Lead[]][]).map(([etapa, ls]) => [
          etapa,
          ls.filter((l) => l.nome.toLowerCase().includes(busca.toLowerCase()))
        ])
      ) as Record<PipelineEtapa, Lead[]>)
    : byEtapa;

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (isError)
    return <Alert severity="error">Erro ao carregar leads. Verifique sua conexão.</Alert>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Pipeline de Vendas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total} leads ·{' '}
            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              valorTotal
            )}{' '}
            em aberto
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar lead..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" onClick={() => setNewLeadOpen(true)}>
            + Novo Lead
          </Button>
        </Box>
      </Box>

      {/* Kanban */}
      <KanbanBoard
        byEtapa={filteredByEtapa}
        onCardClick={setSelectedLead}
        onEtapaChange={(id, etapa) => updateEtapa.mutate({ id, etapa })}
      />

      {/* Dialogs */}
      <LeadDetailDialog
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        onEdit={(lead) => {
          setSelectedLead(null);
          setEditLead(lead);
        }}
      />

      <LeadForm
        open={newLeadOpen}
        onClose={() => setNewLeadOpen(false)}
        onSubmit={async (data) => {
          await createLead.mutateAsync(data);
        }}
      />

      {editLead && (
        <LeadForm
          open={!!editLead}
          onClose={() => setEditLead(null)}
          onSubmit={async (data) => {
            await updateLead.mutateAsync({ id: editLead.id, ...data });
          }}
          defaultValues={editLead}
          title="Editar Lead"
        />
      )}
    </Box>
  );
}
